import { lazy, type ComponentType } from "react";

/**
 * Détecte une erreur de chargement de chunk (import dynamique). Après un
 * déploiement, l'index.html en cache référence d'anciens chunks hashés qui
 * n'existent plus → l'`import()` rejette avec « Failed to fetch dynamically
 * imported module » (ou variantes selon le navigateur).
 */
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to fetch/i.test(
    msg,
  );
}

const RELOAD_KEY = "chunk-reload-at";
const RELOAD_GUARD_MS = 10_000;

/** Recharge la page une fois (anti-boucle). Retourne true si le reload est lancé. */
function reloadOnce(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  } catch {
    /* sessionStorage indisponible : on tente quand même une fois */
  }
  if (Date.now() - last < RELOAD_GUARD_MS) return false; // anti-boucle
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  window.location.reload();
  return true;
}

/**
 * `React.lazy` durci contre les chunks périmés : si l'import dynamique échoue
 * pour cause de chunk manquant (nouveau déploiement), on recharge la page une
 * fois pour récupérer le nouvel index.html au lieu d'afficher une erreur.
 */
export function lazyWithReload<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>,
>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      // Chunk périmé → on recharge ; on garde le fallback Suspense affiché
      // (promesse en attente) le temps que la navigation se fasse, au lieu de
      // laisser l'error boundary flasher.
      if (isChunkLoadError(err) && reloadOnce()) {
        return new Promise<never>(() => {});
      }
      throw err;
    }),
  );
}
